import { useState } from 'react';
import { useIntl } from 'react-intl';

import { Study } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { useAppDispatch } from '@/shared/hooks';
import { debug, delay, getCardRemovalAnimationDuration } from '@/shared/lib';

import { ModalID, modalModel } from '@/entities/modal';
import { studyModel } from '@/entities/study';

export const useRawMeshViewerCardActions = (study: Study) => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const [isRemoving, setIsRemoving] = useState(false);
  const { CanDelete = false } = study.YourPermissions ?? {};

  const openRemoveConfirmModal = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.Confirm,
        data: {
          title: formatMessage({
            id: 'removeReportModal.title',
            defaultMessage: 'Remove report',
          }),
          okText: formatMessage({
            id: 'global.applyRemove',
            defaultMessage: 'Yes, remove',
          }),
          description: formatMessage({
            id: 'removeReportModal.message',
            defaultMessage:
              'After removing, all report data will be lost. Are you sure?',
          }),
          danger: true,
          onConfirm: async () => {
            dispatch(modalModel.actions.closeModal(ModalID.Confirm));
            setIsRemoving(true);

            try {
              await delay(getCardRemovalAnimationDuration());
              await dispatch(
                studyModel.thunks.unsetStudyRawMeshViewer({
                  StudyID: study.ID,
                }),
              ).unwrap();
            } catch (error) {
              setIsRemoving(false);
              debug('Failed to unset raw mesh viewer:', error);
            }
          },
        },
      }),
    );
  };

  const openInfoModal = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.RawMeshViewerInfo,
        data: {
          studyID: study.ID,
          canDelete: CanDelete,
          onRemove: openRemoveConfirmModal,
        },
      }),
    );
  };

  return { isRemoving, CanDelete, openRemoveConfirmModal, openInfoModal };
};
